import moment from "moment";

import type {MomentDateRange} from "./types.ts";

export const DEMO_DATE_RANGE: MomentDateRange = {
    startDate: moment("2024-07-21"),
    endDate: moment("2024-08-21"),
};

export const CLASS_SNAPSHOT_DEMO_DATE_RANGE: MomentDateRange = {
    startDate: moment("2024-07-21"),
    endDate: moment("2024-08-21"),
};

function shiftDateBackByDays(date: moment.Moment, days: number): moment.Moment {
    return date.clone().subtract(days, "days");
}

/**
 * Gets the offset in days between the provided date and today
 * the time and timezone will be discarded
 */
function getDateOffsetInDays(date: moment.Moment): number {
    const today = moment().startOf("day");
    return today.diff(date, "days");
}

/** Moves a date into the pre-defined demo data date range
 *
 * If forceShift is true, it will shift the date even if it already
 * falls within the demo range.
 * This is required by shiftDateRangeToDemoDateRange to shift a
 * start date that happens to already fall within the demo range,
 * but still needs to be adjusted.
 *
 * If shiftBack is true, it will shift the date back from the demo range
 * to the original range.
 */
export function shiftDateForDemoRange(
    date?: moment.Moment | null,
    forceShift?: boolean,
    shiftBack?: boolean,
): moment.Moment | null | undefined {
    if (!date) {
        return undefined;
    }
    if (
        !forceShift &&
        date.isSameOrAfter(DEMO_DATE_RANGE.startDate) &&
        date.isSameOrBefore(DEMO_DATE_RANGE.endDate)
    ) {
        // The date is already within the range, return it as is
        return date;
    }

    if (!forceShift && date.isBefore(DEMO_DATE_RANGE.startDate)) {
        // the date is before the date range
        // since we don't know where in the requested range
        // this date falls, we just return the demo data start date
        return DEMO_DATE_RANGE.startDate.clone();
    }

    // How far are we currently from the end of the demo date range
    let dateOffsetInDays = getDateOffsetInDays(DEMO_DATE_RANGE.endDate);

    if (shiftBack) {
        dateOffsetInDays = dateOffsetInDays * -1;
    }

    return shiftDateBackByDays(date, dateOffsetInDays);
}

/** Takes a date range (start & end date) and returns an equivalent range
 * that's within the pre-defined demo data date range.
 *
 * The date shift is based off of the end date in the date range. So the
 * new end date will always be the same as the end date of the demo data
 * date range.
 */
export function shiftDateRangeToDemoDateRange(
    dateRange?: MomentDateRange | null,
): MomentDateRange | null | undefined {
    // Currently all of our demo data is between Dec XX, 2022 and XXX XX, 202X
    // Shift the selected date range so it's within this date range
    if (!dateRange?.startDate || !dateRange?.endDate) {
        return undefined;
    }

    if (
        dateRange.endDate.isSameOrAfter(DEMO_DATE_RANGE.startDate) &&
        dateRange.endDate.isSameOrBefore(DEMO_DATE_RANGE.endDate)
    ) {
        // The selected range is within the demo data range,
        // Return it unchanged
        return dateRange;
    }

    if (dateRange.endDate.isSameOrBefore(DEMO_DATE_RANGE.startDate)) {
        // The selected range is BEFORE the demo data range
        // Move it forward
        const rangeLengthInDays = dateRange.endDate.diff(
            dateRange.startDate,
            "days",
        );

        return {
            startDate: DEMO_DATE_RANGE.endDate
                .clone()
                .subtract(rangeLengthInDays, "days"),
            endDate: DEMO_DATE_RANGE.endDate.clone(),
        };
    }

    const shiftedStartDate = shiftDateForDemoRange(dateRange.startDate, true);
    const shiftedEndDate = shiftDateForDemoRange(dateRange.endDate);

    if (!shiftedStartDate || !shiftedEndDate) {
        return undefined;
    }

    return {
        startDate: shiftedStartDate,
        endDate: shiftedEndDate,
    };
}
